<script lang="ts" setup>
import { Comment, Fragment, Text, computed, getCurrentInstance, useAttrs, useSlots, type VNode } from 'vue';
import { formControlDisabledSurfaceClasses, formControlDisabledClasses, formChoiceRowBaseClasses, formChoiceRowDisabledClasses, formChoiceRowInteractiveClasses } from '../form-control/classes';
defineOptions({
    name: 'IdeoFormCheckbox',
    inheritAttrs: false
});

const model = defineModel<any>({
    required: false,
    default: null,
    set(value)
    {
        emit('change', value);

        return value;
    }
});
const props = withDefaults(defineProps<{
    name?: string;
    checked?: any;
    value?: any;
    uncheckedValue?: any;
    indeterminate?: boolean;
    switch?: boolean;
    disabled?: boolean;
    inline?: boolean;
    noPadding?: boolean;
    compact?: boolean;
    card?: boolean;
    ariaLabel?: string;
}>(), {
    name: null,
    checked: undefined,
    value: true,
    uncheckedValue: false,
    indeterminate: false,
    switch: false,
    disabled: false,
    inline: false,
    noPadding: false,
    compact: false,
    card: false,
    ariaLabel: undefined
});

const emit = defineEmits<{
    (e: 'change', state: boolean): void;
    (e: 'click', event: any): void;
}>();

const attrs = useAttrs();

function rootAttrs()
{
    const result = { ...attrs };

    delete result['aria-describedby'];
    delete result['aria-invalid'];

    return result;
}

const instance = getCurrentInstance();
const slots = useSlots();
const uid = computed(() => instance.uid.toString());
const input = ref<HTMLInputElement | null>(null);
const helpId = computed(() => `ideo-form-check-${uid.value}-help`);
function hasContent(nodes: VNode[]): boolean
{
    return nodes.some(node =>
    {
        if (node.type === Comment)
            return false;

        if (node.type === Fragment && Array.isArray(node.children))
            return hasContent(node.children as VNode[]);

        return node.type !== Text || String(node.children ?? '').trim().length > 0;
    });
}

// Sloty mogą pozostać zdefiniowane bez treści, a ich obecność nie jest reaktywna dla computed
function hasDefaultSlot()
{
    return hasContent(slots.default?.() || []);
}

function hasHelpSlot()
{
    return hasContent(slots.help?.() || []);
}
function containsDescriptionId(nodes: VNode[], ids: string[]): boolean
{
    return nodes.some(node =>
    {
        if (typeof node.props?.id === 'string' && ids.includes(node.props.id))
            return true;

        return Array.isArray(node.children) && containsDescriptionId(node.children as VNode[], ids);
    });
}

function describedBy()
{
    const external = attrs['aria-describedby'];
    const ids = typeof external === 'string' ? external.split(/\s+/u).filter(Boolean) : [];

    const help = slots.help?.() || [];

    if (hasContent(help) && !containsDescriptionId(help, ids))
        ids.push(helpId.value);

    return [...new Set(ids)].join(' ') || undefined;
}

const internal = computed<any>({
    get()
    {
        return props.checked === undefined ? model.value : props.checked;
    },
    set(value)
    {
        if (props.checked === undefined)
            model.value = value;
        else
            emit('change', value);
    }
});
const proxy = computed<boolean>({
    get()
    {
        if (Array.isArray(internal.value))
        {
            return internal.value.includes(props.value);
        }
        else
        {
            return Object.is(internal.value, props.value);
        }
    },
    set(value)
    {
        if (Array.isArray(internal.value))
        {
            let items = internal.value.filter(p => !Object.is(p, props.value));

            if (value)
                items = [...items, props.value];

            internal.value = items;
        }
        else
        {
            internal.value = value ? props.value : props.uncheckedValue;
        }
    }
});

// Wartość opcji na elemencie, gdy przełącznik jest jedną z opcji pola-tablicy (grupa, pętla przełączników).
// Narzędzie agenta rozpoznaje po niej grupę i klika opcje, które człowiek widzi; w drzewie dokumentu tej
// wartości nie ma inaczej, bo element wiąże z modelem stan zaznaczenia, a nie wartość opcji
function choiceValue()
{
    return Array.isArray(internal.value) ? JSON.stringify(props.value) : undefined;
}

function onClick(e: any)
{
    emit('click', e);
}

const customClass = () => [
    'ideo-form-check group/form-choice',
    formChoiceRowBaseClasses,
    props.disabled ? formChoiceRowDisabledClasses : props.card ? 'cursor-pointer' : formChoiceRowInteractiveClasses,
    ...(props.card && props.disabled ? formControlDisabledSurfaceClasses : []),
    props.card
        ? proxy.value
            ? 'border-[color-mix(in_oklch,var(--color-accent)_40%,transparent)] bg-accent-soft !text-accent-strong dark:bg-accent-soft-dark dark:!text-accent-strong-dark'
            : 'border-border bg-surface dark:border-border-dark dark:bg-surface-dark'
        : '',
    {
        'mb-0 border px-[10px] py-[7px]': props.card,
        'items-center': true,
        'inline-flex': props.inline === true,
        'flex': props.inline !== true,
        'ideo-form-switch': props.switch === true,
        'mb-0': hasHelpSlot() || props.noPadding || (props.compact && props.switch) || !hasDefaultSlot(),
        'min-h-[28px] px-[8px] py-[6px]': !props.card && hasDefaultSlot() && (!props.noPadding || (props.compact && props.switch)),
        'mb-[2px]': !hasHelpSlot() && !props.card && !props.noPadding && !(props.compact && props.switch) && hasDefaultSlot()
    }
];

</script>

<template>
    <span class="group/form-choice-help min-w-0 max-w-full" :class="hasHelpSlot() ? props.inline ? 'inline-grid align-top' : 'grid' : 'contents'" data-form-check-layout :data-form-switch="props.switch ? '' : undefined">
        <label v-bind="rootAttrs()" :for="uid" :class="customClass()">
            <span :class="[
                props.switch ? 'relative flex h-[15px] w-[26px] shrink-0 items-center' : 'relative flex size-[15px] shrink-0 items-center justify-center'
            ]">
                <input
                    :id="uid"
                    ref="input"
                    v-model="proxy"
                    class="ideo-form-check-input peer m-0 shrink-0 cursor-pointer appearance-none outline-none focus-visible:border-accent dark:focus-visible:border-accent focus-visible:ring-[3px] focus-visible:ring-[color-mix(in_oklch,var(--color-accent)_30%,transparent)] disabled:cursor-not-allowed"
                    :class="[props.switch ? 'disabled:!opacity-100 disabled:border disabled:border-solid disabled:!border-field-disabled-border disabled:!bg-field-disabled-bg dark:disabled:!border-field-disabled-border-dark dark:disabled:!bg-field-disabled-bg-dark' : formControlDisabledClasses, props.switch ? 'ideo-form-check-switch h-[15px] w-[26px] rounded-full border-0 bg-border-strong [transition:background_0.18s_cubic-bezier(.2,.8,.3,1),box-shadow_0.1s] checked:bg-accent motion-reduce:transition-none dark:bg-border-strong-dark dark:checked:bg-accent' : 'size-[15px] rounded-[4px] border-[1.4px] border-border-strong bg-surface [transition:background_0.15s,border-color_0.15s,box-shadow_0.1s] checked:border-accent checked:bg-accent indeterminate:border-accent indeterminate:bg-accent dark:border-border-strong-dark dark:bg-surface-dark dark:checked:border-accent dark:checked:bg-accent dark:indeterminate:border-accent dark:indeterminate:bg-accent']"
                    type="checkbox"
                    :indeterminate.prop="props.indeterminate"
                    :name="props.name"
                    :data-choice-value="choiceValue()"
                    :aria-label="props.ariaLabel"
                    :aria-describedby="describedBy()"
                    :aria-invalid="attrs['aria-invalid']"
                    :disabled="props.disabled"
                    @click.stop="onClick"
                >
                <span
                    v-if="props.switch"
                    class="pointer-events-none absolute start-[2px] size-[11px] rounded-full bg-white [transition:translate_0.18s_cubic-bezier(.2,.8,.3,1)] peer-checked:translate-x-[11px] peer-disabled:opacity-100 motion-reduce:transition-none dark:bg-[oklch(0.2_0.01_260)]"
                />
                <IdeoIcon
                    v-else-if="props.indeterminate"
                    name="minus"
                    class="pointer-events-none absolute text-white peer-disabled:opacity-100 peer-disabled:text-field-disabled-fg dark:text-black dark:peer-disabled:text-field-disabled-fg-dark"
                    :size="11"
                />
                <IdeoIcon
                    v-else-if="proxy"
                    name="check"
                    class="pointer-events-none absolute text-white peer-disabled:opacity-100 peer-disabled:text-field-disabled-fg dark:text-black dark:peer-disabled:text-field-disabled-fg-dark"
                    :size="11"
                />
            </span>
            <span v-if="hasDefaultSlot()" class="ideo-form-check-label group-has-[input:disabled]/form-choice:opacity-80 mb-0 min-w-0 flex-1 text-ui-body/normal" :class="props.compact && props.switch ? 'ms-[9px]' : 'ms-[8px]'">
                <slot name="default" />
            </span>
        </label>
        <span v-if="hasHelpSlot()" :id="helpId" class="ideo-form-check-help mt-[4px] block min-w-0 max-w-full text-start text-ui-helper font-[400] text-fg-faint group-has-[input:disabled]/form-choice-help:opacity-80 dark:text-fg-faint-dark">
            <slot name="help" />
        </span>
    </span>
</template>
